import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Lightning,
  ShieldCheck,
  CloudArrowUp,
  ArrowsOutCardinal,
  ArrowRight,
} from "@phosphor-icons/react";

const heroImage =
  "https://images.unsplash.com/photo-1561233835-f937539b95b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGNsb3VkJTIwc2VydmVyJTIwZGF0YWNlbnRlcnxlbnwwfHx8fDE3ODEwNzcyMzJ8MA&ixlib=rb-4.1.0&q=85";

const teamImage =
  "https://images.unsplash.com/photo-1681949287382-052ea3954a51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxzdGFydHVwJTIwdGVhbSUyMG1vZGVybiUyMG9mZmljZXxlbnwwfHx8fDE3ODEwNzcyMzF8MA&ixlib=rb-4.1.0&q=85";

const stats = [
  { value: "99.999%", label: "Uptime SLA" },
  { value: "500M+", label: "Requests handled daily" },
  { value: "<10ms", label: "Average edge latency" },
];

const services = [
  {
    icon: CloudArrowUp,
    title: "Cloud Orchestration",
    desc: "Automated deployment pipelines and zero-downtime scaling across every region you serve.",
  },
  {
    icon: Lightning,
    title: "DevOps Automation",
    desc: "Continuous integration and delivery frameworks tailored for engineering velocity.",
  },
  {
    icon: ShieldCheck,
    title: "Zero-Trust Security",
    desc: "Military-grade encryption, granular access control, and policy enforced at the edge.",
  },
  {
    icon: ArrowsOutCardinal,
    title: "Elastic Scaling",
    desc: "Infrastructure that expands predictively as your traffic spikes — never reactively.",
  },
];

const principles = [
  { num: "01", label: "Performance" },
  { num: "02", label: "Security" },
  { num: "03", label: "Reliability" },
  { num: "04", label: "Velocity" },
];

const partners = [
  "ATLAS LABS",
  "NORTHWIND",
  "MERIDIAN AI",
  "OBSCURA",
  "HELIX TECH",
  "QUANTUMCORE",
  "VERIDIAN",
  "ARCWAVE",
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative border-b border-zinc-200 overflow-hidden">
        <div className="absolute inset-0 grid-noise opacity-60 pointer-events-none" />
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 relative">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="eyebrow mb-8"
              data-testid="hero-overline"
            >
              // INFRASTRUCTURE, REDEFINED
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.95] tracking-tight"
              data-testid="hero-headline"
            >
              Scale Without
              <br />
              Limits.{" "}
              <span className="text-blue-700">Secure</span>
              <br />
              Without Compromise.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mt-8 max-w-xl text-lg text-zinc-600 leading-relaxed"
            >
              Infranova Solutions delivers modern, enterprise-grade cloud
              architecture for the world&apos;s most ambitious operations. Fast,
              resilient, and built for 2026 and beyond.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <Link
                to="/contact"
                data-testid="hero-deploy-cta"
                className="group inline-flex items-center gap-2 bg-blue-700 text-white px-6 py-3.5 text-sm font-medium hover:bg-blue-800 transition-colors"
              >
                Deploy Now
                <ArrowUpRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-docs-cta"
                className="inline-flex items-center gap-2 bg-white border border-zinc-300 text-zinc-950 px-6 py-3.5 text-sm font-medium hover:bg-zinc-50 transition-colors"
              >
                Read the Documentation
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] border border-zinc-200 overflow-hidden bg-zinc-50">
              <img
                src={heroImage}
                alt="Illuminated server architecture"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white/15 via-transparent to-white/5" />
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/90 bg-zinc-950/80 px-2 py-1">
                  Region · us-east-1
                </span>
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/90 bg-blue-700/90 px-2 py-1">
                  ● live
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 border border-zinc-200 p-4">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  edge.latency.p99
                </div>
                <div className="font-display text-3xl mt-1">
                  8.4<span className="text-zinc-400 text-xl">ms</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* LOGOS + STATS */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
          <p className="eyebrow text-center text-zinc-500">
            Trusted by engineering teams at leading enterprises
          </p>
          <div className="mt-8 overflow-hidden ticker-mask">
            <div className="flex gap-12 animate-marquee whitespace-nowrap">
              {[...partners, ...partners].map((p, i) => (
                <span
                  key={i}
                  className="font-display font-black text-2xl tracking-tight text-zinc-300 hover:text-zinc-500 transition-colors"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t border-zinc-200">
          <div className="mx-auto max-w-[1400px] grid grid-cols-1 md:grid-cols-3">
            {stats.map((s, i) => (
              <div
                key={s.label}
                className={`p-8 lg:p-12 ${i < 2 ? "md:border-r border-zinc-200" : ""} ${i > 0 ? "border-t md:border-t-0 border-zinc-200" : ""}`}
                data-testid={`hero-stat-${i}`}
              >
                <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-zinc-500">
                  0{i + 1} / Metric
                </div>
                <div className="mt-3 font-display text-5xl lg:text-6xl tracking-tight">
                  {s.value}
                </div>
                <div className="mt-2 text-sm text-zinc-600">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROP */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-6">Why Infranova</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-3xl">
              Engineering precision meets startup agility.
            </h2>
            <p className="mt-8 text-lg text-zinc-600 leading-relaxed max-w-2xl">
              We stripped away the legacy overhead. What remains is a pure,
              high-performance infrastructure layer designed specifically for
              modern technical directors — built by senior architects who
              actually shipped at scale.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 border-t border-l border-zinc-200">
              {principles.map((p) => (
                <div
                  key={p.num}
                  className="border-r border-b border-zinc-200 p-6 lg:p-8"
                >
                  <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-blue-700">
                    {p.num}
                  </div>
                  <div className="mt-3 font-display text-xl">{p.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div>
              <div className="eyebrow mb-4">Capabilities</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-2xl">
                Core infrastructure capabilities.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="services-overview-link"
              className="inline-flex items-center gap-2 text-sm font-medium text-zinc-950 hover:text-blue-700 transition-colors group"
            >
              See all capabilities
              <ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-zinc-200 bg-white">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.title}
                  data-testid={`service-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="border-r border-b border-zinc-200 p-8 lg:p-10 group hover:bg-zinc-50 transition-colors"
                >
                  <Icon
                    size={28}
                    weight="duotone"
                    className="text-blue-700"
                  />
                  <h3 className="mt-6 font-display text-2xl tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-zinc-600 leading-relaxed max-w-md">
                    {s.desc}
                  </p>
                  <Link
                    to="/services"
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 group-hover:gap-2.5 transition-all"
                  >
                    Explore <ArrowRight size={14} weight="bold" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ABOUT SNIPPET */}
      <section className="border-b border-zinc-200">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative aspect-[5/4] border border-zinc-200 overflow-hidden">
              <img
                src={teamImage}
                alt="The Infranova engineering team"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-white/95 border-t border-zinc-200 p-5">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  the team / 2026
                </div>
                <div className="mt-1 font-display text-xl">
                  50 brilliant minds. One stack to redefine.
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="eyebrow mb-6">The Team</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              Built by engineers, for operations.
            </h2>
            <p className="mt-8 text-lg text-zinc-600 leading-relaxed">
              Launched in March 2026, we&apos;ve rapidly grown to a 50-person
              collective of the sharpest minds in distributed systems. We
              don&apos;t just host — we engineer the backbone of your business.
            </p>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="mt-8 inline-flex items-center gap-2 bg-zinc-950 text-white px-6 py-3.5 text-sm font-medium hover:bg-zinc-800 transition-colors"
            >
              Meet the team
              <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="bg-zinc-950 text-white">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-blue-400 mb-6">
              // The Next Step
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.02] tracking-tight">
              Ready to rebuild your stack?
            </h2>
            <p className="mt-6 text-lg text-zinc-300 max-w-2xl leading-relaxed">
              Get a custom infrastructure assessment from our senior
              architects within 24 hours.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link
              to="/contact"
              data-testid="home-contact-cta"
              className="group inline-flex items-center gap-3 bg-blue-700 text-white px-7 py-4 text-base font-medium hover:bg-blue-600 transition-colors"
            >
              Contact Engineering
              <ArrowUpRight size={18} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
